import { AlertTriangle, Info, Megaphone, X } from "lucide-react";
import { useSiteConfig } from "@/contexts/SiteConfigContext";

const SEVERITY_STYLES = {
    info: {
        wrap: "border-[#00BF0D]/25 bg-[#00BF0D]/[0.07] text-[#00BF0D]",
        icon: Info,
        title: "text-white/95",
        body: "text-white/70",
    },
    warning: {
        wrap: "border-amber-500/25 bg-amber-500/[0.08] text-amber-300",
        icon: AlertTriangle,
        title: "text-amber-50/95",
        body: "text-amber-100/75",
    },
    critical: {
        wrap: "border-rose-500/30 bg-rose-500/[0.1] text-rose-200",
        icon: AlertTriangle,
        title: "text-rose-50",
        body: "text-rose-100/80",
    },
};

function AnnouncementItem({ announcement, onDismiss }) {
    const severity = SEVERITY_STYLES[announcement.severity] || SEVERITY_STYLES.info;
    const Icon = severity.icon;

    return (
        <div
            className={`site-announcement flex items-start gap-3 border-b px-3 py-2.5 sm:px-5 ${severity.wrap}`}
            role="status"
            data-testid={`site-announcement-${announcement.id}`}
        >
            <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <div className="min-w-0 flex-1">
                <p className={`text-sm font-medium ${severity.title}`}>{announcement.title}</p>
                <p className={`mt-0.5 text-xs leading-relaxed ${severity.body}`}>{announcement.message}</p>
                {announcement.link_url ? (
                    <a
                        href={announcement.link_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-1.5 inline-flex text-xs font-medium underline underline-offset-2 hover:text-white"
                    >
                        {announcement.link_label || "Learn more"}
                    </a>
                ) : null}
            </div>
            {announcement.dismissible ? (
                <button
                    type="button"
                    onClick={() => onDismiss(announcement.id)}
                    className="btn-ghost shrink-0 rounded-md p-1.5 text-current/70 hover:text-white"
                    aria-label="Dismiss announcement"
                    data-testid={`site-announcement-dismiss-${announcement.id}`}
                >
                    <X className="h-4 w-4" />
                </button>
            ) : null}
        </div>
    );
}

/** Full-width announcement strip rendered at the top of the shell. */
export function SiteAnnouncementBar({ audience = "app" }) {
    const { announcements, dismissAnnouncement } = useSiteConfig();
    const visible = announcements.filter(
        (item) => item.audience === "all" || item.audience === audience,
    );

    if (!visible.length) return null;

    return (
        <div className="site-announcement-bar shrink-0 border-b border-white/[0.06]" data-testid="site-announcement-bar">
            {visible.length === 1 ? null : (
                <div className="flex items-center gap-2 border-b border-white/[0.06] bg-white/[0.02] px-3 py-1.5 sm:px-5">
                    <Megaphone className="h-3.5 w-3.5 text-white/45" aria-hidden />
                    <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/45">
                        Platform announcements
                    </p>
                </div>
            )}
            {visible.map((announcement) => (
                <AnnouncementItem
                    key={announcement.id}
                    announcement={announcement}
                    onDismiss={dismissAnnouncement}
                />
            ))}
        </div>
    );
}
